import { Stack } from "@phosphor-icons/react";
import { useQueries, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { StatCard } from "@/components/StatCard";
import { EmptyState } from "@/components/states";
import { Skeleton } from "@/components/ui/skeleton";
import { sessionQuery } from "@/features/auth/queries";
import { useSession } from "@/features/auth/useSession";
import type { MasterType } from "@/features/master-types/api";
import { masterTypeIndexQuery } from "@/features/master-types/queries";
import { recordCountQuery } from "@/features/records/queries";

/**
 * Which master types this person can work with.
 *
 * A grant is per master type, so "Records" is not one list — it is a
 * section per type the caller holds `record.read` on. Filtering here is
 * cosmetic, as always: the server refuses a type they have no grant on,
 * and hiding it only saves them the click.
 *
 * DEE-65 gave each type a count and a name filter. **No per-status
 * breakdown chips** ("5 approved · 2 pending"): that needs a grouped
 * count the API does not expose, and DEE-58 settled it in favour of
 * building what the API already supports. Do not add them client-side —
 * the page holds no records to count.
 */
export const Route = createFileRoute("/_authed/records/")({
  staticData: { crumbs: [{ key: "nav.records" }] },
  loader: async ({ context }) => {
    // The session is already in cache — `_authed`'s own loader primed it —
    // so this resolves without a request and gives the locale the index
    // has to be keyed on.
    const me = await context.queryClient.ensureQueryData(sessionQuery);
    await context.queryClient.ensureQueryData(masterTypeIndexQuery(me.resolved_locale));
  },
  component: RecordTypes,
});

/** Module-level and pure so it is a stable dependency of the filter memo,
 *  the same shape the dashboard's own `typeName` takes. */
function typeName(type: MasterType, locale: string, defaultLocale: string): string {
  return (
    type.display_name[locale] ??
    type.display_name[defaultLocale] ??
    Object.values(type.display_name)[0] ??
    type.code
  );
}

function RecordTypes() {
  const { t } = useTranslation();
  const { me, can } = useSession();
  const { data: index } = useSuspenseQuery(masterTypeIndexQuery(me.resolved_locale));
  const [filter, setFilter] = useState("");

  const readable = useMemo(
    () => index.all.filter((type) => can("record.read", type.code)),
    [index, can],
  );

  // One count per readable type, the same dedicated endpoint the
  // dashboard uses. Keyset pagination deliberately returns no total, so a
  // count is its own query rather than a field on the list response.
  const counts = useQueries({ queries: readable.map((type) => recordCountQuery(type.code)) });

  const locale = me.resolved_locale;
  const defaultLocale = me.tenant.default_locale;

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle === "") return readable;
    return readable.filter(
      (type) =>
        type.code.toLowerCase().includes(needle) ||
        typeName(type, locale, defaultLocale).toLowerCase().includes(needle),
    );
  }, [readable, filter, locale, defaultLocale]);

  return (
    <>
      <PageHeader
        title={t("records.title")}
        description={t("records.description")}
        help={t("pageHelp.records")}
      />

      {readable.length === 0 ? (
        <EmptyState title={t("records.noTypes.title")} body={t("records.noTypes.body")} />
      ) : (
        <>
          <SearchInput
            label={t("records.filterTypes")}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder={t("records.filterTypes")}
            containerClassName="mb-3 max-w-sm"
          />

          {filtered.length === 0 ? (
            // Filters matched nothing, which is not the same as holding no
            // grants — the empty state above offers no way forward, and
            // this one does, because clearing the box is the way forward.
            <p className="text-muted text-sm">{t("table.noResults.title")}</p>
          ) : (
            <ul className="space-y-2">
              {filtered.map((type) => {
                const query = counts[readable.indexOf(type)];
                return (
                  <li key={type.id}>
                    <StatCard
                      icon={Stack}
                      label={typeName(type, locale, defaultLocale)}
                      subtext={type.code}
                      to="/records/$code"
                      params={{ code: type.code }}
                      value={
                        query?.isPending !== false ? (
                          <Skeleton className="h-6 w-10" />
                        ) : query.isError ? (
                          <span className="text-muted text-sm">
                            {t("dashboard.countUnavailable")}
                          </span>
                        ) : (
                          (query.data ?? 0).toLocaleString(me.resolved_locale)
                        )
                      }
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </>
  );
}
